import { FEATURES, METADATA } from './config/app-config';
import { useAppConfig } from './config/AppConfigContext';
/**
 * COPA-DATA Docs – Frontend
 *
 * Main application shell for the online help SPA.
 * ------------------------------------------------
 * - Uses Material-UI for layout & theming
 * - Manages global application state that does not fit into lower-level components
 *   (dark-mode preference, navigation drawer width, current article selection, etc.)
 * - Orchestrates routing / legacy-link handling and persists user preferences
 *
 * NOTE:  This file purposefully keeps side-effects (e.g. localStorage writes,
 *        URL synchronisation) close to where the state is defined so that
 *        behaviour is transparent and easy to audit.
 */

import React, {
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react';
import {
    CssBaseline,
    Box,
    Toolbar,
} from '@mui/material';
import HeaderBar from './components/navigation/header/HeaderBar';
import SideNavigation from './components/navigation/SideNavigation';
import ReaderPanel from './components/reader/ReaderPanel';
import SingleReaderPanel from './components/reader/SingleReaderPanel';
import useContent from './hooks/useContent/useContent';
import OfflineSnackbar from './components/notifications/OfflineSnackbar';
import LegacyLoader from './components/dialogs/LegacyLoader';
import { useLegacyLinkResolver } from './utils/legacyLinkResolver';
import { ThemeProvider } from '@mui/material/styles';
import { ReleaseOptionsProvider } from './contexts/ReleaseOptionsContext';
import { LLMProcessProvider } from './contexts/LLMProcessContext';

import LoggedOutSplash from './components/auth/LoggedOutSplash';

import { createAppTheme } from './config/theme';
import { applyColorScheme } from './config/apply-runtime-branding';

/* ------------------------------------------------------------------------- */
/* --- Layout Constants – bounds for the resizable side-navigation bar.     */
/* ------------------------------------------------------------------------- */
const MIN_NAV = 150;          // User experience tested: below this the tree is unusable
const MAX_NAV = 700;          // Hard limit; prevents overlaying the reader panel
const DEFAULT_NAV = 350;      // Reasonable default that shows 2–3 levels of TOC

/** Clamp helper for safely persisting the nav width in localStorage. */
function clamp(v) {
    return Math.min(MAX_NAV, Math.max(MIN_NAV, v));
}

/**
 * <App />
 *
 * Root component rendered by index.jsx.  Everything that should survive a
 * client-side route change lives here (theme, TOC & article cache, etc.).
 */
function App() {
    const config = useAppConfig();
    /* -----------------------------------------------------------------------
     * Global UI state
     * -------------------------------------------------------------------- */
    const [mobileOpen, setMobileOpen] = useState(false);          // Drawer toggle on <= sm breakpoints

    /* STEP 0: Read query params immediately so that render decisions (single-page
     * reader, etc.) are synchronous. */
    const params = new URLSearchParams(window.location.search);
    const isSingleMode = params.get('single') === 'true';         // “/reader?single=true” opens isolated reader

    /* Dark-mode – persisted preference in localStorage -------------------- */
    const [darkMode, setDarkMode] = useState(() => {
        /* Lazy initializer → read once per page-load to avoid unnecessary work. */
        // If themes are deactivated always apply light mode
        if (FEATURES.themes === false) return false;

        const saved = localStorage.getItem('darkMode');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const toggleDarkMode = () => setDarkMode(prev => !prev);

    /* Persist dark-mode on every change. */
    useEffect(() => {
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
        applyColorScheme(config, darkMode ? 'dark' : 'light');
    }, [darkMode, config]);

    const theme = useMemo(
        () => createAppTheme(config, darkMode ? 'dark' : 'light'),
        [config, darkMode],
    );

    /* STEP 1: Detect “legacy” URL pattern from zenon 11/Service Engine help
     *         (“…/Content.html” or “…/Content/”).  URL gets resolved => SPA path. */
    const isLegacyPath = /\/Content(?:\.html)?\/?$/i.test(window.location.pathname);

    /* STEP 2: Load core content state via custom hook.  It encapsulates:
     * - TOC fetch & nesting
     * - Article caching and in-view detection
     * - Version / language / product selection
     * - Offline mode flag (future feature)
     *
     * When we are on a legacy path we skip the automatic article selection until
     * the resolver has finished (otherwise the wrong article could flash). */
    const {
        tocData,
        flatToc,
        articles,
        currentArticleIndex,
        selectedArticleId,
        selectedRelease,
        setSelectedRelease,
        handleArticleSelect,
        loadNextArticle,
        loadPrevArticle,
        handleArticleInView,
        isLoading,
        error,
        offlineMode,
        fetchArticle,
    } = useContent({ skipInitialSelect: isLegacyPath });

    /* STEP 3:  Resolve deep links from the old help system.
     * - Translates query parameters → modern IDs (language, version, product).
     * - After resolving it selects the corresponding article through the hook.
     * waitingForLegacy = true while resolution is in progress.  */
    const { waitingForLegacy } = useLegacyLinkResolver({
        isLegacyPath,
        flatToc,
        handleArticleSelect,
    });

    /* Hamburger icon toggles drawer in mobile / tablet breakpoints. */
    const handleDrawerToggle = () => setMobileOpen(o => !o);

    /* -----------------------------------------------------------------------
     * URL synchronisation – keeps query string in sync with current selection
     * so that the user can bookmark / share links. Skipped when:
     *   - legacy mapping still pending OR
     *   - no article selected yet OR
     *   - reader opened in single-page embed mode (no outer navigation).
     * -------------------------------------------------------------------- */
    useEffect(() => {
        if (waitingForLegacy || !selectedArticleId || isSingleMode) return;

        const qs = selectedRelease.toSearchParams();
        qs.set('articleId', selectedArticleId);

        window.history.replaceState(null, '', `?${qs.toString()}`);
    }, [
        waitingForLegacy,
        selectedArticleId,
        selectedRelease,
        isSingleMode,
    ]);

    /* Document title – update tab text for better accessibility / history. */
    useEffect(() => {
        if (waitingForLegacy || !selectedArticleId) return;
        const current = flatToc.find(item => item._id === selectedArticleId);
        document.title = current?.title
            ? `${current.title} | ${METADATA.title}`
            : `${METADATA.title}`;
    }, [waitingForLegacy, selectedArticleId, flatToc]);

    /* -----------------------------------------------------------------------
     * Side-navigation width – resizable splitter persisted in localStorage
     * -------------------------------------------------------------------- */
    const [navWidth, setNavWidth] = useState(() => {
        const saved = parseInt(localStorage.getItem('tocWidth') ?? '', 10);
        return Number.isFinite(saved) ? clamp(saved) : DEFAULT_NAV;
    });

    /** Handler passed down to the splitter component inside <SideNavigation /> */
    const handleNavResize = useCallback(width => {
        const clamped = clamp(width);
        setNavWidth(clamped);
        localStorage.setItem('tocWidth', clamped);
    }, []);

    /* -----------------------------------------------------------------------
     * EARLY RETURNS
     * -------------------------------------------------------------------- */

    /**
     * Logged-out route handling
     * ------------------------
     * We render a tiny splash page that tells the user they are signed out and
     * then redirects back to the start page after a short delay.
     *
     * IMPORTANT:
     * This check intentionally lives here (AFTER all Hooks above were called)
     * so React's “Rules of Hooks” are not violated. If we returned earlier,
     * some renders would execute fewer Hooks than others, which triggers
     * eslint's react-hooks/rules-of-hooks error.
     */
    if (window.location.pathname === '/auth/logged-out') {
        return (
            <ThemeProvider theme={theme}>
                <LoggedOutSplash />
            </ThemeProvider>
        );
    }

    /** While resolving legacy links we show a tiny loader to avoid layout shift. */
    if (waitingForLegacy) {
        return (
            <ThemeProvider theme={theme}>
                <LegacyLoader />
            </ThemeProvider>
        );
    }

    /** Single-reader mode – used for iframe embeds (no TOC / header). */
    if (isSingleMode) {
        return (
            <ThemeProvider theme={theme}>
                <Box
                    sx={{
                        height: '100vh',
                        width: '100vw',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <CssBaseline />
                    <SingleReaderPanel
                        fetchArticle={fetchArticle}
                        isSinglePageView={true}
                    />
                </Box>
            </ThemeProvider>
        );
    }

    /* -----------------------------------------------------------------------
     * DEFAULT LAYOUT
     * -------------------------------------------------------------------- */
    return (
        <ThemeProvider theme={theme}>
            <ReleaseOptionsProvider selectedRelease={selectedRelease} setSelectedRelease={setSelectedRelease}>
                <LLMProcessProvider>
                    {/* Root flex container fills viewport height */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                        <CssBaseline />

                        {/* Snackbar pops up when offlineMode flag turns true */}
                        <OfflineSnackbar offlineMode={offlineMode} />

                        {/* Top application bar – handles global actions & search */}
                        <HeaderBar
                            handleDrawerToggle={handleDrawerToggle}
                            selectedRelease={selectedRelease}
                            setSelectedRelease={setSelectedRelease}
                            handleArticleSelect={handleArticleSelect}
                            fetchArticle={fetchArticle}
                            isLoading={isLoading}
                            offlineMode={offlineMode}
                        />

                        {/* Spacer so that content does not hide behind the fixed <AppBar /> */}
                        <Toolbar />

                        {/* Main two-pane layout: Side-navigation + Reader */}
                        <Box
                            component="div"
                            className={darkMode ? 'dark-mode' : ''}
                            sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}
                        >
                            {/* ---------------- Side Navigation (TOC tree) ---------------- */}
                            <Box
                                component="nav"
                                sx={{
                                    width: { sm: navWidth },
                                    flexShrink: { sm: 0 },
                                    borderRight: { xs: 'none', sm: '1px solid #ddd' },
                                    overflow: 'hidden',
                                    minHeight: 0,
                                }}
                            >
                                <SideNavigation
                                    mobileOpen={mobileOpen}
                                    handleDrawerToggle={handleDrawerToggle}
                                    tocData={tocData}
                                    onArticleSelect={handleArticleSelect}
                                    selectedArticleId={selectedArticleId}
                                    selectedRelease={selectedRelease}
                                    drawerWidth={navWidth}
                                    onResize={handleNavResize}
                                    toggleDarkMode={toggleDarkMode}
                                />
                            </Box>

                            {/* ---------------- Main Reader Pane ---------------- */}
                            <Box
                                component="main"
                                sx={{
                                    flexGrow: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <ReaderPanel
                                    tocData={tocData}
                                    flatToc={flatToc}
                                    articles={articles}
                                    selectedArticleId={selectedArticleId}
                                    currentArticleIndex={currentArticleIndex}
                                    selectedRelease={selectedRelease}
                                    loadNextArticle={loadNextArticle}
                                    loadPrevArticle={loadPrevArticle}
                                    onArticleChange={handleArticleInView}
                                    handleArticleSelect={handleArticleSelect}
                                    isLoading={isLoading}
                                    error={error}
                                    offlineMode={offlineMode}
                                    fetchArticle={fetchArticle}
                                />
                            </Box>
                        </Box>
                    </Box>
                </LLMProcessProvider>
            </ReleaseOptionsProvider>
        </ThemeProvider>
    );
}

export default App;
